/** Diff viewer for comparing two wiki revisions. Simple side-by-side diff view. */
import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { api } from '../../auth/api';
import { ArrowLeft } from 'lucide-react';

export default function WikiDiff() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const fromRev = searchParams.get('from');
    const toRev = searchParams.get('to');
    const [oldData, setOldData] = useState(null);
    const [newData, setNewData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                // Fetch the two revisions
                const [oldRes, newRes] = await Promise.all([
                    api.get(`/wiki/entry/${id}/history/revision`, { params: { rev: fromRev } }),
                    api.get(`/wiki/entry/${id}/history/revision`, { params: { rev: toRev } }),
                ]);
                setOldData(oldRes.data);
                setNewData(newRes.data);
            } catch (e) {
                setError('Failed to load revisions. The backend endpoints may need to be implemented.');
            }
            setLoading(false);
        };
        load();
    }, [id, fromRev, toRev]);

    const fields = [
        { key: 'title_en', label: 'Title (EN)' },
        { key: 'title_vi', label: 'Title (VI)' },
        { key: 'overview_en', label: 'Overview (EN)' },
        { key: 'overview_vi', label: 'Overview (VI)' },
        { key: 'capabilities_en', label: 'Capabilities (EN)' },
        { key: 'capabilities_vi', label: 'Capabilities (VI)' },
        { key: 'vulnerabilities_en', label: 'Vulnerabilities (EN)' },
        { key: 'vulnerabilities_vi', label: 'Vulnerabilities (VI)' },
    ];

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="flex items-center gap-3 mb-6">
                <Link to={`/wiki/entry/${id}/history`} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
                    <ArrowLeft size={16} /> Back to History
                </Link>
                <span className="text-slate-600">|</span>
                <h1 className="text-lg font-bold text-white">Comparing Revisions</h1>
                <span className="text-sm text-slate-400">#{fromRev} → #{toRev}</span>
            </div>

            {error && <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{error}</div>}

            {loading ? (
                <div className="text-slate-400 text-sm">Loading diff...</div>
            ) : oldData && newData ? (
                <div className="space-y-4">
                    {fields.map(f => {
                        const oldVal = oldData[f.key] || '';
                        const newVal = newData[f.key] || '';
                        if (oldVal === newVal) return null;
                        return (
                            <DiffBlock key={f.key} label={f.label} oldVal={oldVal} newVal={newVal} />
                        );
                    })}
                    <NoChanges oldData={oldData} newData={newData} fields={fields} />
                </div>
            ) : (
                <div className="text-slate-400 text-sm">No data available for comparison.</div>
            )}
        </div>
    );
}

function DiffBlock({ label, oldVal, newVal }) {
    const oldLines = oldVal.split('\n');
    const newLines = newVal.split('\n');

    return (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-slate-800 border-b border-slate-700 text-sm font-medium text-amber-400">
                {label}
            </div>
            <div className="grid grid-cols-2 divide-x divide-slate-700">
                <div className="p-3">
                    <div className="text-xs text-slate-500 mb-1">Old (#{oldVal.substring(0, 20)})</div>
                    {oldLines.map((line, i) => (
                        <div key={i} className="text-sm text-red-300 bg-red-500/5 px-2 py-0.5 -mx-2 border-l-2 border-red-500/50 whitespace-pre-wrap">
                            {line || ' '}
                        </div>
                    ))}
                </div>
                <div className="p-3">
                    <div className="text-xs text-slate-500 mb-1">New (#{newVal.substring(0, 20)})</div>
                    {newLines.map((line, i) => (
                        <div key={i} className="text-sm text-green-300 bg-green-500/5 px-2 py-0.5 -mx-2 border-l-2 border-green-500/50 whitespace-pre-wrap">
                            {line || ' '}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function NoChanges({ oldData, newData, fields }) {
    const hasChanges = fields.some(f => oldData[f.key] !== newData[f.key]);
    if (hasChanges) return null;
    return (
        <div className="p-4 rounded bg-slate-800/50 border border-slate-700 text-center text-slate-400 text-sm">
            No text field differences found between these revisions. (Client fit and compliance changes may not be shown.)
        </div>
    );
}